export interface SearchFilters {
  query: string
  minPrice?: number
  maxPrice?: number
}

function parseMoney(value?: string | null) {
  if (!value) return undefined
  const cleaned = value.replace(/[^0-9.]/g, '')
  if (!cleaned) return undefined
  const amount = Number(cleaned)
  return Number.isFinite(amount) ? amount : undefined
}

export function parseSearchParams(params: URLSearchParams): SearchFilters {
  const query = (params.get('q') || '').trim()
  const minPrice = parseMoney(params.get('min'))
  const maxPrice = parseMoney(params.get('max'))
  return { query, minPrice, maxPrice }
}

export function buildSearchPath(filters: SearchFilters) {
  const params = new URLSearchParams()
  if (filters.query.trim()) params.set('q', filters.query.trim())
  if (filters.minPrice != null) params.set('min', String(filters.minPrice))
  if (filters.maxPrice != null) params.set('max', String(filters.maxPrice))
  const qs = params.toString()
  return qs ? `/search?${qs}` : '/search'
}

export function extractPriceHint(query: string) {
  const text = query.trim()
  const range = text.match(/\$?\s*(\d+(?:\.\d+)?)\s*[-to]+\s*\$?\s*(\d+(?:\.\d+)?)/i)
  if (range) {
    return {
      leftover: text.replace(range[0], ' ').replace(/\s+/g, ' ').trim(),
      minPrice: Number(range[1]),
      maxPrice: Number(range[2]),
    }
  }

  const under = text.match(/(?:under|below|less than|<)\s*\$?\s*(\d+(?:\.\d+)?)/i)
  if (under) {
    return {
      leftover: text.replace(under[0], ' ').replace(/\s+/g, ' ').trim(),
      maxPrice: Number(under[1]),
    }
  }

  const over = text.match(/(?:over|above|more than|>)\s*\$?\s*(\d+(?:\.\d+)?)/i)
  if (over) {
    return {
      leftover: text.replace(over[0], ' ').replace(/\s+/g, ' ').trim(),
      minPrice: Number(over[1]),
    }
  }

  const exact = text.match(/\$\s*(\d+(?:\.\d+)?)/)
  if (exact) {
    const amount = Number(exact[1])
    return {
      leftover: text.replace(exact[0], ' ').replace(/\s+/g, ' ').trim(),
      minPrice: amount,
      maxPrice: amount,
    }
  }

  return { leftover: text }
}

export function matchesKeyword(haystack: Array<string | undefined>, keyword: string) {
  if (!keyword) return true
  const needle = keyword.toLowerCase()
  return haystack.some(value => (value || '').toLowerCase().includes(needle))
}

export function matchesPrice(price: number | undefined, minPrice?: number, maxPrice?: number) {
  if (minPrice == null && maxPrice == null) return true
  if (price == null || Number.isNaN(Number(price))) return false
  const amount = Number(price)
  if (minPrice != null && amount < minPrice) return false
  if (maxPrice != null && amount > maxPrice) return false
  return true
}
